/**
 * Web Push registration + tiny notification sound helper.
 *
 *   await ensurePushPermission()  → boolean (granted/denied)
 *   await subscribeToPush()       → subscribes the browser to our VAPID server
 *   playNotificationSound()       → quick 2-tone chime via WebAudio (no asset)
 */
import { api } from '@/lib/api';

const PUBLIC_VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js');
    return reg;
  } catch (e) {
    console.warn('SW register failed', e);
    return null;
  }
}

export async function ensurePushPermission() {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  const res = await Notification.requestPermission();
  return res;
}

export async function subscribeToPush() {
  if (!PUBLIC_VAPID_KEY) return { ok: false, reason: 'no-vapid-key' };
  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: 'no-sw' };
  const perm = await ensurePushPermission();
  if (perm !== 'granted') return { ok: false, reason: 'permission-' + perm };
  try {
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY),
      });
    }
    await api.post('/push/subscribe', { subscription: sub.toJSON() });
    return { ok: true };
  } catch (e) {
    console.warn('Push subscribe failed', e);
    return { ok: false, reason: 'subscribe-failed', error: e?.message };
  }
}

export async function unsubscribeFromPush() {
  if (!('serviceWorker' in navigator)) return false;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post('/push/unsubscribe', { subscription: sub.toJSON() }); } catch (e) { if (e) console.warn(e); }
    await sub.unsubscribe();
  }
  return true;
}

// Tiny 2-tone chime — used for in-app notifications without any audio file.
//
// iOS audio quirks handled here (iter-89):
//   - Safari / iOS WebView starts every AudioContext in `suspended` state.
//   - It will only resume after a USER GESTURE (tap/click/keydown). Calling
//     `playNotificationSound()` from a background poll therefore silently
//     no-ops on iOS unless the context was already unlocked.
//   - We register a one-shot global gesture listener that resumes the context
//     and plays an inaudible blip so subsequent calls (from the notification
//     poller) actually emit sound.
let _audioCtx = null;
let _audioUnlocked = false;

function _getCtx() {
  if (_audioCtx) return _audioCtx;
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _audioCtx = new Ctx();
  return _audioCtx;
}

function _unlockAudio() {
  const ctx = _getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  // Inaudible blip — iOS treats this as the gesture-initiated playback.
  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.01);
    _audioUnlocked = true;
  } catch (_e) { /* ignore */ }
}

if (typeof window !== 'undefined') {
  const onGesture = () => {
    _unlockAudio();
    if (_audioUnlocked) {
      window.removeEventListener('touchend', onGesture, true);
      window.removeEventListener('click', onGesture, true);
      window.removeEventListener('keydown', onGesture, true);
    }
  };
  window.addEventListener('touchend', onGesture, true);
  window.addEventListener('click', onGesture, true);
  window.addEventListener('keydown', onGesture, true);
}

export function playNotificationSound() {
  try {
    if (typeof window === 'undefined') return;
    const ctx = _getCtx();
    if (!ctx) return;
    // Always try to resume — Safari may have auto-suspended on background.
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    const now = ctx.currentTime;
    const play = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.02);
    };
    play(880, 0, 0.18);
    play(1320, 0.12, 0.22);
  } catch (e) {
    // Audio autoplay may be blocked before user interaction — warn only.
    if (e) console.warn('push notification chime suppressed:', e.message || e);
  }
}
